

import { useState, useEffect , useRef} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useDispatch, useSelector } from "react-redux";


import {
  getHowToTakeAction,
  deleteHowToTakeAction,
} from "../../../store/actions/HowToTakeAction";

const HowToTake = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isFirstLoad = useRef(true);

  // ✅ REDUX STATE
  const howToTakeList = useSelector((state) => state.howToTake.list);
  const numRows = useSelector((state) => state.howToTake.num_rows);

  const [loading, setLoading] = useState(false);
  const [searchQueries, setSearchQueries] = useState({
    how_to_take_text: "",
    how_to_take_status: "",
  });

  const [sortField, setSortField] = useState("how_to_take_text");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  const [searchText, setSearchText] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  let searchTimeout = null;

  const fetchHowToTakeFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ Show loader only on first visit
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getHowToTakeAction(
          pagination,
          pageid,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch how to take:", err);
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever
    }
  };
  

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
  
    const clearedQueries = {
      how_to_take_text: "",
      how_to_take_status: "",
    };
  
    const updatedQueries = { ...clearedQueries, [field]: keyword };
  
    setSearchQueries(updatedQueries);
    setCurrentPage(0);
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
  
    // ✅ debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchHowToTakeFromServer(
        field,
        keyword,
        true,
        0,
        sortField,
        sortOrder
      );
    }, 400);
  };
  
  // ================= SORT =================
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);
    fetchHowToTakeFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order);
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This entry will be deleted permanently.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deleteHowToTakeAction(id));
          // Refresh the current page after deletion
          await fetchHowToTakeFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // ================= PAGINATION =================
  const totalPages = Math.ceil(numRows / rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
      fetchHowToTakeFromServer(activeSearchField, activeSearchKeyword, true, page, sortField, sortOrder);
    }
  };

  
  useEffect(() => {
 
    if (location?.state?.returnTo) {
      const { currentPage: prevPage, searchQueries: prevSearches, sortField: prevSort, sortOrder: prevOrder } = location.state.returnTo;
      setCurrentPage(prevPage || 0);
      setSearchQueries(prevSearches || {});
      setSortField(prevSort || "how_to_take_text");
      setSortOrder(prevOrder || "asc");

      const searchField = prevSearches?.how_to_take_text ? "how_to_take_text" : prevSearches?.how_to_take_status ? "how_to_take_status" : "";
      const searchKeyword = prevSearches?.how_to_take_text || prevSearches?.how_to_take_status || "";

      setActiveSearchField(searchField);
      setActiveSearchKeyword(searchKeyword);

      // Fetch with restored state
      fetchHowToTakeFromServer(
        searchField,
        searchKeyword,
        true,
        prevPage || 0,
        prevSort || "how_to_take_text",
        prevOrder || "asc"
      );
    } else {
      fetchHowToTakeFromServer();
    }
  }, []);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link
            to="/add-how-to-take"
            state={{
              returnTo: {
                currentPage,
                searchQueries,
                sortField,
                sortOrder,
              },
            }}
            className="btn btn-primary btn-rounded"
          >
            + Add How To Take
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center my-5">
              <Loader size="lg" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th onClick={() => handleSort("how_to_take_text")} style={{ cursor: "pointer" }}>
                        How To Take {sortField === "how_to_take_text" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Search text"
                          value={searchQueries.how_to_take_text}
                          onChange={(e) =>
                            handleSearch(e, "how_to_take_text")
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>

                      <th onClick={() => handleSort("how_to_take_status")} style={{ cursor: "pointer" }}>
                        Status {sortField === "how_to_take_status" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Search status"
                          value={searchQueries.how_to_take_status}
                          onChange={(e) =>
                            handleSearch(e, "how_to_take_status")
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>

                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {howToTakeList.map((item) => (
                      <tr key={item.id}>
                        <td>{item.text}</td>
                        <td>
  <span
    className={`badge ${
      item.status === "Active"
        ? "bg-success"
        : "bg-danger"
    }`}
  >
    {item.status}
  </span>
</td>


                        <td>
                          <div className="d-flex">
                            <button
                              className="btn btn-link p-0 me-3"
                              onClick={() =>
                                navigate("/add-how-to-take", {
                                  state: {
                                    item: {
                                      how_to_take_id: item.id,
                                      how_to_take_text: item.text,
                                      how_to_take_status: item.status,
                                    },
                                    returnTo: {
                                      currentPage,
                                      searchQueries,
                                      sortField,
                                      sortOrder,
                                    },
                                  },
                                })
                              }
                              
                            >
                              <i className="fa fa-pencil-alt text-success fs-18"></i>
                            </button>

                            <button
                              className="btn btn-link p-0"
                              onClick={() => handleDelete(item.id)}
                            >
                              <i className="fa fa-trash text-danger fs-18"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {howToTakeList.length === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center">
                          No How To Take found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div
                className="pagination-wrapper"
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "20px",
                  flexWrap: "wrap",
                  flexDirection: "column",
                }}
              >
                <div>
                  Showing {howToTakeList?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                  {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                  <button
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                    disabled={currentPage === 0}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === 0 ? "not-allowed" : "pointer",
                      opacity: currentPage === 0 ? 0.5 : 1,
                    }}
                  >
                    &lt;
                  </button>
                  {(() => {
                    const pages = [];
                    const maxPagesToShow = 5;
                    const startPage = Math.max(
                      0,
                      Math.min(currentPage - 2, totalPages - maxPagesToShow)
                    );
                    const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                    if (startPage > 0) {
                      pages.push(
                        <button
                          key={0}
                          onClick={() => handlePageChange(0)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          1
                        </button>
                      );
                      if (startPage > 1) {
                        pages.push(
                          <span
                            key="dots1"
                            style={{ padding: "6px 12px", userSelect: "none" }}
                          >
                            ...
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i < endPage; i++) {
                      const isActive = currentPage === i;
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: isActive ? "#007bff" : "#fff",
                            color: isActive ? "#fff" : "#000",
                            cursor: "pointer",
                          }}
                        >
                          {i + 1}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span
                            key="dots2"
                            style={{ padding: "6px 12px", userSelect: "none" }}
                          >
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages - 1}
                          onClick={() => handlePageChange(totalPages - 1)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                  <button
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                    disabled={currentPage === totalPages - 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                      opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    }}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default HowToTake;
